import axios from 'axios';
import { REST_API_END_POINT } from '../../../../constants/DefaultValues';

const CACHE_TTL_MS = 75000;
const cache = new Map();
const inFlightPrefetches = new Map();

export function normalizeInstallationId(installationId) {
  if (installationId == null || installationId === 99) return 99;
  return Number(installationId);
}

export function getTicketsListCacheKey({ userId, userType, installationId, tab, listView }) {
  const inst = normalizeInstallationId(installationId);
  const tabKey = tab || 'active';
  const lv = listView ? '1' : '0';
  return `${userId}-${userType}-${inst}-${tabKey}-${lv}`;
}

export function buildTicketsListRequestBody({
  permission,
  userId,
  userType,
  installationId,
  tab,
  listView = false,
  filterPayload,
  page,
  rowsPerPage,
}) {
  const inst = installationId != null ? installationId : null;
  return {
    permission,
    userId,
    userType,
    installationId: inst,
    tab: tab || 'active',
    ...(listView ? { listView: true } : {}),
    filterPayload,
    page,
    rowsPerPage,
  };
}

export function getCachedTicketsList(cacheKey) {
  const entry = cache.get(cacheKey);
  if (!entry) return null;
  if (Date.now() - entry.timestamp > CACHE_TTL_MS) {
    cache.delete(cacheKey);
    return null;
  }
  return entry.data;
}

export function setCachedTicketsList(cacheKey, data) {
  if (!cacheKey || !data) return;
  cache.set(cacheKey, { data, timestamp: Date.now() });
}

export function invalidateTicketsListCache(cacheKey) {
  if (cacheKey) {
    cache.delete(cacheKey);
    return;
  }
  cache.clear();
}

export async function fetchTicketsList(requestBody) {
  const res = await axios.post(
    `${REST_API_END_POINT}tickets/get-all-tickets`,
    requestBody,
  );
  return res?.data?.data || { task: [], totalCount: 0, statusCountMap: {} };
}

export function prefetchTicketsList(params) {
  const requestBody = buildTicketsListRequestBody(params);
  const cacheKey = getTicketsListCacheKey({
    userId: params.userId,
    userType: params.userType,
    installationId: params.installationId,
    tab: params.tab,
    listView: params.listView,
  });

  const cached = getCachedTicketsList(cacheKey);
  if (cached) return Promise.resolve(cached);

  if (inFlightPrefetches.has(cacheKey)) {
    return inFlightPrefetches.get(cacheKey);
  }

  const fetchPromise = fetchTicketsList(requestBody)
    .then((data) => {
      setCachedTicketsList(cacheKey, data);
      return data;
    })
    .catch(() => null)
    .finally(() => {
      inFlightPrefetches.delete(cacheKey);
    });

  inFlightPrefetches.set(cacheKey, fetchPromise);
  return fetchPromise;
}

export function prefetchTicketsPageRoute() {
  import('../../CrmTickets');
}

export function prefetchTicketsPageFromSession() {
  try {
    const userSession = JSON.parse(localStorage.getItem('user'));
    if (!userSession?.user_id) return;
    prefetchTicketsPageRoute();
    prefetchTicketsList({
      permission: userSession.user_permissions,
      userId: userSession.user_id,
      userType: userSession.user_type,
      installationId: 99,
      tab: 'active',
      listView: true,
      page: 0,
      rowsPerPage: 10,
    });
  } catch {
    // ignore
  }
}
